<template>
  <div class="drops-wrapper">
    <div class="info-bar">
      <div class="online-info" aria-live="polite">
        <i class="fa-solid fa-people-group" aria-hidden="true"></i>
        <span class="online">{{ onlineFormatted }}</span>
        <span class="online-label">{{ t('DROPS.online') }}</span>
      </div>

      <div class="menu" role="tablist" :aria-label="t('DROPS.aria.tablist')">
        <!-- Live (lightning) -->
        <div
          class="menu-box"
          :class="{ active: mode === 'live' }"
          role="tab"
          :aria-selected="mode === 'live'"
          :aria-pressed="mode === 'live'"
          tabindex="0"
          :title="t('DROPS.tabs.live')"
          :aria-label="t('DROPS.tabs.live')"
          @click="setMode('live')"
          @keydown.enter.prevent="setMode('live')"
          @keydown.space.prevent="setMode('live')"
        >
          <i class="fa-solid fa-bolt" aria-hidden="true"></i>
        </div>

        <!-- Best (crown/web-awesome) -->
        <div
          class="menu-box"
          :class="{ active: mode === 'best' }"
          role="tab"
          :aria-selected="mode === 'best'"
          :aria-pressed="mode === 'best'"
          tabindex="0"
          :title="t('DROPS.tabs.best')"
          :aria-label="t('DROPS.tabs.best')"
          @click="setMode('best')"
          @keydown.enter.prevent="setMode('best')"
          @keydown.space.prevent="setMode('best')"
        >
          <i class="fa-solid fa-web-awesome" aria-hidden="true"></i>
        </div>
      </div>
    </div>

    <TransitionGroup name="drop-slide" tag="div" class="drop-list">
      <LiveDropItem v-for="drop in displayedDrops" :data="drop" :key="drop.id" />
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useStatsStore } from '@/store/stats'
import { useI18n } from 'vue-i18n'

// Models
import { LiveDrop } from '@/services/models/models'
// Components
import LiveDropItem from '@/components/LiveDropItem/LiveDropItem.vue'

const { t } = useI18n()

defineOptions({ name: 'Drops' })

// Store
const stats = useStatsStore()
const { drops, luckyDrops, onlineUsers } = storeToRefs(stats)

// Local state
type Mode = 'live' | 'best'
const mode = ref<Mode>('live')

// Derived
const displayedDrops = computed<LiveDrop[]>(() =>
  mode.value === 'best'
    ? ((luckyDrops.value as LiveDrop[]) ?? [])
    : ((drops.value as LiveDrop[]) ?? []),
)

const onlineFormatted = computed(() => ((onlineUsers.value ?? 0) as number).toLocaleString('en'))

// Actions
function setMode(next: Mode) {
  mode.value = next
}
</script>

<style scoped lang="scss">
.drops-wrapper {
  display: flex;
  background-color: $elevation1;
  width: 100%;
  height: 120px;
  gap: 6px;
  padding: 4px;

  .info-bar {
    display: flex;
    gap: 3px;

    .online-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
      width: 70px;
      height: 100%;
      background-color: $elevation2;
      border: 1px solid transparent;

      i {
        width: 20px;
        height: 20px;
        color: $text-light-100;
      }

      .online {
        font-size: 14px;
        color: #fff;
        font-weight: 600;
        letter-spacing: 0.2px;
      }

      .online-label {
        font-size: 10px;
        color: $text-light-200;
        font-weight: 500;
        letter-spacing: 0.4px;
        text-transform: uppercase;
      }
    }

    .menu {
      display: flex;
      flex-direction: column;
      gap: 3px;

      .menu-box {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 60px;
        height: 60px;
        background-color: $elevation2;
        cursor: pointer;
        user-select: none;
        border: 1px solid transparent;
        outline: none;
        transition:
          transform 0.15s ease,
          box-shadow 0.15s ease,
          border-color 0.15s ease,
          background-color 0.15s ease;

        i {
          width: 20px;
          height: 20px;
          color: $text-light-100;
        }

        &.active {
          border-color: rgba($accent-yellow, 0.65);
          box-shadow:
            0 0 0 2px rgba($accent-yellow, 0.2) inset,
            0 0 18px rgba($accent-yellow, 0.18);
          background-image: linear-gradient(rgba($accent-yellow, 0.12), rgba($accent-yellow, 0.02));

          i {
            color: $accent-yellow;
            text-shadow: 0 0 8px rgba($accent-yellow, 0.5);
          }
        }
      }
    }
  }

  .drop-list {
    display: flex;
    gap: 6px;
    width: 100%;
    overflow: hidden; /* horizontal strip look */
    align-items: center;
  }
}

// Entrance animation for drops
.drop-slide-enter-active {
  transition:
    transform 0.2s ease-out,
    opacity 0.2s ease-out;
}

.drop-slide-enter-from {
  transform: translateX(-120px);
  opacity: 0;
}

.drop-slide-enter-to {
  transform: translateX(0);
  opacity: 1;
}

.drop-slide-move {
  transition: transform 0.2s ease-out;
}

@media (max-width: 600px) {
  .drops-wrapper {
    display: none;
  }
}
</style>
